import {
  Filter,
  FilterField,
  OutputField,
  OutputPreviewField,
  OutputReport,
  PreviewContractReport,
  PreviewFilter,
  ReportEntity,
  ReportTreeValue,
  TreeFieldTypes,
  FilterSign
} from './reports.interfaces';

export class Report {
  public columns: OutputField[] = [];
  public filters: FilterField[] = [];

  constructor(public reportEntity: ReportEntity) {
    this.generateColumnFields();
    this.generateFilterFields();
  }

  public addColumns(treeValues: ReportTreeValue[]): boolean {
    let isAllAdded: boolean = true;
    treeValues.forEach((value: ReportTreeValue) => {
      const isExist = this.columns.find((column: OutputField) => column.field === value.name);
      if (!isExist) {
        this.columns.push(this.getDefaultColumn(
          value.transIds,
          value.name,
          value.rootKey === TreeFieldTypes.EMPLOYEE ? TreeFieldTypes.CONTRACT : value.rootKey,
          value.types,
          '',
          this.columns.length,
          1,
          value.path,
          value.metadatas,
          value.groupZoneType,
          false,
          value.isRetro));
      } else {
        isAllAdded = false;
      }
    });
    return isAllAdded;
  }

  public addFilters(treeValues: ReportTreeValue[]): boolean {
    let isAllAdded: boolean = true;
    treeValues.forEach((value: ReportTreeValue) => {
      const isExist = this.filters.find((filter: FilterField) => filter.field === value.name);
      if (!isExist) {
        this.filters.push(this.getDefaultFilter(
          value.transIds,
          value.name,
          value.rootKey === TreeFieldTypes.EMPLOYEE ? TreeFieldTypes.CONTRACT : value.rootKey,
          value.types[0], // TODO: Will be handled properly in a future story
          [],
          value.unitTransId,
          value.metadatas,
          value.path,
          value.groupZoneType,
          this.filters.length
        ));
      } else {
        isAllAdded = false;
      }
    });
    return isAllAdded;
  }

  public getOutputReport(): OutputReport {
    const filterFields: { [key: string]: FilterField } = {};
    const outputFields: { [key: string]: OutputField } = {};

    this.filters.forEach((filter: FilterField, index: number) => {
      filterFields[filter.field] = {
        filterDocument: filter.filterDocument,
        fieldType: filter.fieldType,
        fieldGroupType: filter.fieldGroupType,
        fieldPosition: index,
        field: filter.field,
        filter: filter.filter,
        unitTransId: filter.unitTransId,
        transIds: filter.transIds,
        metadatas: filter.metadatas,
        path: filter.path
      };
    });

    this.columns.forEach((column: OutputField, index: number) => {
      outputFields[column.field] = {
        filterDocument: column.filterDocument,
        fieldTypes: column.fieldTypes,
        fieldGroupType: column.fieldGroupType,
        fieldSortedBy: column.fieldSortedBy,
        fieldSortPosition: column.fieldSortPosition,
        fieldPosition: index,
        field: column.field,
        transIds: column.transIds,
        path: column.path,
        metadatas: column.metadatas,
        displayRetro: column.displayRetro,
        displayEntityPath: column.displayEntityPath,
        isRetro: column.isRetro
      };
    });

    return {
      id: this.reportEntity.id,
      cliCoreId: this.reportEntity.cliCoreID,
      authorId: this.reportEntity.authorId,
      beginDate: this.reportEntity.beginDate,
      endDate: this.reportEntity.endDate,
      dateOption: this.reportEntity.dateOption,
      filterFields: filterFields,
      outputFields: outputFields,
      timeFilter: this.reportEntity.timeFilter,
      editingAllowed: this.reportEntity.editingAllowed ? this.reportEntity.editingAllowed : false,
      isPublic: this.reportEntity.isPublic,
      isStandard: this.reportEntity.isStandard,
      title: this.reportEntity.title,
      massActionsEnabled: this.reportEntity.massActionsEnabled
    };
  }

  public getPreviewReport(): PreviewContractReport {
    const filterParameters: PreviewFilter[] = [];
    const outputFilters: OutputPreviewField[] = [];
    let isOnlyEntity: boolean = true;

    this.filters.forEach((filter: FilterField) => {
      if (filter.metadatas.disabled) {
        return;
      }

      if (filter.filterDocument !== TreeFieldTypes.ENTITY) {
        isOnlyEntity = false;
      }

      filterParameters.push({
        filterDocument: filter.filterDocument,
        fieldType: filter.fieldType === 'PAY_VALUE' ? filter.fieldType + '_SUM' : filter.fieldType,
        fieldGroupType: filter.fieldGroupType,
        field: filter.field,
        operatorValueList: filter.filter.map((element: Filter) => {
          return {
            operator: this.convertEmptyMatchOperator(element.sign),
            value: element.value,
            display: element.display
          };
        })
      });
    });

    this.columns.forEach((column: OutputField, index: number) => {
      if (column.metadatas.disabled) {
        return;
      }

      if (column.filterDocument !== TreeFieldTypes.ENTITY) {
        isOnlyEntity = false;
      }

      outputFilters.push({
        filterDocument: column.filterDocument,
        fieldTypes: column.fieldTypes.includes('PAY_VALUE') ? ['PAY_VALUE_SUM'] : column.fieldTypes,
        fieldGroupType: column.fieldGroupType,
        fieldSortedBy: column.fieldSortedBy,
        fieldSortPosition: column.fieldSortPosition,
        fieldPosition: index,
        field: column.field,
        displayRetro: !!column.displayRetro,
        displayEntityPath: !!column.displayEntityPath,
        transIds: column.transIds
      });
    });

    return {
      cliCoreId: this.reportEntity.cliCoreID,
      beginDate: this.reportEntity.beginDate,
      endDate: this.reportEntity.endDate,
      filterDocument: isOnlyEntity ? TreeFieldTypes.ENTITY : TreeFieldTypes.CONTRACT,
      filterParameters: filterParameters,
      outputFilters: outputFilters
    };
  }

  private convertEmptyMatchOperator(filterSign: FilterSign): FilterSign {
    if (filterSign === FilterSign.EM) {
      return FilterSign.EQ;
    } else if (filterSign === FilterSign.NEM) {
      return FilterSign.NE;
    } else {
      return filterSign;
    }
  }

  private generateColumnFields(): void {
    this.columns = this.reportEntity.outputFields.map((output: OutputField) => {
      return this.getDefaultColumn(
        output.transIds,
        output.field,
        output.filterDocument,
        output.fieldTypes,
        output.fieldSortedBy,
        output.fieldPosition,
        output.fieldSortPosition,
        output.path,
        output.metadatas,
        output.fieldGroupType,
        output.displayRetro,
        output.displayEntityPath);
    });
  }

  private generateFilterFields(): void {
    this.filters = [...this.reportEntity.filterFields]
      .sort((a: FilterField, b: FilterField) => (a.fieldPosition || 0) - (b.fieldPosition || 0))
      .map((filter: FilterField, index: number) => {
        return this.getDefaultFilter(
          filter.transIds,
          filter.field,
          filter.filterDocument,
          filter.fieldType,
          filter.filter,
          filter.unitTransId,
          filter.metadatas,
          filter.path,
          filter.fieldGroupType,
          index);
      });
  }

  private getDefaultColumn(transIds: string[] = [],
                           field: string = '',
                           filterDocument: string = '',
                           fieldTypes: string[] = [],
                           fieldSortedBy: string = '',
                           fieldPosition: number = 1,
                           fieldSortPosition: number = 1,
                           path: string[][] = [],
                           metadatas: any = {},
                           fieldGroupType?: string | string[],
                           displayRetro?: boolean,
                           displayEntityPath?: boolean,
                           isRetro?: boolean): OutputField {

    return {
      transIds,
      field,
      filterDocument,
      fieldTypes,
      fieldGroupType,
      fieldSortedBy,
      fieldPosition,
      fieldSortPosition,
      path,
      metadatas,
      displayRetro,
      displayEntityPath,
      isRetro
    };
  }

  private getDefaultFilter(transIds: string[] = [],
                           field: string = '',
                           filterDocument: string = '',
                           fieldType: string = '',
                           filter: any[] = [],
                           unitTransId: string = '',
                           metadatas: any = {},
                           path: string[][] = [],
                           fieldGroupType?: string | string[],
                           fieldPosition: number = 0): FilterField {
    return {
      transIds,
      field,
      filterDocument,
      fieldType,
      fieldGroupType,
      fieldPosition,
      filter,
      unitTransId,
      metadatas,
      path
    };
  }
}
